import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Sun, MapPin, Loader2, Crosshair, Search, X } from "lucide-react";

/**
 * Weather Power strip — iter 28.2 / 31.24 (international).
 *
 * Per No. 6: a sunny day today affects TOMORROW's productivity. Two
 * sunny in a row = full power. Two dull = zero. The score is strictly
 * lagged on the backend (sunshine yesterday + day-before / 16h).
 *
 * Iter 31.24 — audience is international, so the UK-postcode requirement
 * is gone. New flow (best practice):
 *   1. Backend silently IP-geolocates on first /weather/power call —
 *      strip just renders with the detected location.
 *   2. "Use precise location" button triggers browser Geolocation API
 *      (with permission) to upgrade IP-level to GPS-level accuracy.
 *   3. "Change" opens a city-name search powered by Open-Meteo
 *      geocoding (international, free, no key).
 */
export default function WeatherStrip() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    const [err, setErr] = useState("");
    const [saving, setSaving] = useState(false);

    // Place-search state
    const [showSearch, setShowSearch] = useState(false);
    const [searchQ, setSearchQ] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const searchTimer = useRef(null);

    // Tuning
    const [tuningOpen, setTuningOpen] = useState(false);
    const [tuningInput, setTuningInput] = useState("");

    const load = async () => {
        const t0 = Date.now();
        try {
            setErr("");
            const { data } = await api.get("/weather/power");
            setData(data);
        } catch (e) {
            const msg = e?.response?.data?.detail || e.message || "Weather failed";
            setErr(msg);
            // Iter 31.42 — capture silent 504s and Network Errors to
            // /incidents so they surface in the same dashboard the
            // Brief SSE drops do. Mirrors the pattern wired into
            // PatternsStrip and Brief.jsx.
            try {
                await api.post("/admin/incident-log", {
                    kind: "weather_strip_failed",
                    message: msg,
                    path: "/brief",
                    context: {
                        fired_at: new Date().toISOString(),
                        elapsed_ms: Date.now() - t0,
                        http_status: e?.response?.status || null,
                    },
                    user_agent: navigator.userAgent || "",
                });
            } catch (_) { /* incident log itself failed — nothing more we can do */ }
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => { load(); }, []);

    const searchPlaces = (q) => {
        clearTimeout(searchTimer.current);
        if (!q || q.length < 2) {
            setSearchResults([]);
            return;
        }
        searchTimer.current = setTimeout(async () => {
            setSearching(true);
            try {
                const { data } = await api.get("/weather/place-search", { params: { q } });
                setSearchResults(data.results || []);
            } catch (_) {
                setSearchResults([]);
            } finally {
                setSearching(false);
            }
        }, 300);
    };

    const pickPlace = async (place) => {
        setSaving(true);
        try {
            // We already have lat/lon from /weather/place-search — send those
            // plus a clean label so the backend doesn't need to re-geocode
            // (avoids "Lincoln, UK" comma-rejection issues with Open-Meteo).
            await api.post("/weather/location", {
                latitude: place.latitude,
                longitude: place.longitude,
                label: place.label || place.name,
            });
            setShowSearch(false);
            setSearchQ("");
            setSearchResults([]);
            await load();
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const usePreciseLocation = () => {
        if (!("geolocation" in navigator)) {
            setErr("Browser doesn't support precise location.");
            return;
        }
        setSaving(true);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                try {
                    await api.post("/weather/location", {
                        latitude: pos.coords.latitude,
                        longitude: pos.coords.longitude,
                    });
                    await load();
                } catch (e) {
                    setErr(e?.response?.data?.detail || e.message || "Save failed");
                } finally {
                    setSaving(false);
                }            },
            (geoErr) => {
                setSaving(false);
                const msg = geoErr.code === geoErr.PERMISSION_DENIED
                    ? "Permission denied — leaving the IP-detected location in place."
                    : `Geolocation failed: ${geoErr.message}`;
                setErr(msg);
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
        );
    };

    // Iter 31.166 — wipe the stored row so the backend re-derives via
    // the canonical chain (current address → IP → manual). Use this
    // when a previous setup attempt left a malformed row that's
    // causing a 500 on /weather/power.
    const resetLocation = async () => {
        if (!window.confirm("Reset weather location? The backend will re-derive from your current address in /admin/Addresses, or fall back to IP geolocation. Use this if weather is stuck 500-ing.")) return;
        setSaving(true);
        setErr("");
        try {
            await api.post("/weather/reset");
            await load();
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Reset failed");
        } finally {
            setSaving(false);
        }
    };

    const saveTuning = async () => {
        const v = parseFloat(tuningInput);
        if (Number.isNaN(v)) return;
        setSaving(true);
        try {
            await api.post("/weather/tuning", { reference_hours: v });
            setTuningOpen(false);
            setTuningInput("");
            await load();
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Tuning failed");
        } finally {
            setSaving(false);
        }
    };

    if (busy) return null;

    if (err && !data) {
        return (
            <div
                className="paper-card border border-stamp-red/30 p-3 mb-6 font-body text-xs text-stamp-red"
                data-testid="weather-strip-error"
            >
                Weather offline: {err}
            </div>
        );
    }

    // configured=false only fires if IP geolocation also failed. Rare, but
    // honest fallback: city-name search only.
    if (!data?.configured) {
        return (
            <section className="paper-card border border-ink/25 p-5 mb-8" data-testid="weather-strip-setup">
                <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-4 h-4 text-stamp-red" strokeWidth={1.5} />
                    <div className="font-heading uppercase tracking-[0.2em] text-sm text-ink">
                        Weather Power · location needed
                    </div>
                </div>
                <p className="font-body text-xs text-ink-soft mb-3">
                    {data?.message || "Couldn't detect your location automatically. Pick a city, or share precise location."}
                </p>
                <PlaceSearchInput
                    value={searchQ}
                    onChange={(v) => { setSearchQ(v); searchPlaces(v); }}
                    results={searchResults}
                    onPick={pickPlace}
                    searching={searching}
                    saving={saving}
                    autoFocus
                />
                <button
                    onClick={usePreciseLocation}
                    disabled={saving}
                    data-testid="weather-precise-location"
                    className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 border border-stamp-red text-stamp-red font-heading uppercase tracking-[0.18em] text-[11px] hover:bg-stamp-red hover:text-paper disabled:opacity-50"
                >
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Crosshair className="w-3 h-3" />}
                    Use precise location
                </button>
                <button
                    onClick={resetLocation}
                    disabled={saving}
                    data-testid="weather-reset-location"
                    className="mt-3 ml-2 inline-flex items-center gap-2 px-3 py-1.5 border border-ink/40 text-ink-soft font-heading uppercase tracking-[0.18em] text-[11px] hover:border-stamp-red hover:text-stamp-red disabled:opacity-50"
                    title="Wipe the stored row and re-derive from Addresses → IP → manual"
                >
                    Reset & re-derive
                </button>
            </section>
        );
    }

    const today = data.today || {};
    const trend = data.trend || [];
    const score = today.score ?? 0;
    const maxBarHeight = 36;
    const locLabel = data.location?.label || data.location?.city || "—";
    const locFlag = data.location?.country_code
        ? data.location.country_code.toUpperCase()
        : null;

    return (
        <section
            className="paper-card border border-ink/25 p-5 mb-8 relative"
            data-testid="weather-strip"
        >
            <div className="absolute top-3 right-4 classified-stamp pointer-events-none">POWER · LAGGED 48H</div>
            <div className="flex items-center gap-2 mb-3 flex-wrap pr-40">
                <Sun className="w-4 h-4 text-stamp-red" strokeWidth={1.5} />
                <div className="font-heading uppercase tracking-[0.2em] text-sm text-ink">
                    Weather Power
                </div>
                <div
                    className="font-body text-[10px] text-ink-soft uppercase tracking-[0.18em] ml-2 flex items-center gap-1"
                    data-testid="weather-location-label"
                >
                    <MapPin className="w-3 h-3" />
                    {locLabel}
                    {locFlag && <span className="ml-1 text-ink-faint">[{locFlag}]</span>}
                </div>
                {data.location?.auto_detected && data.location?.source === "ip_geolocation" && (
                    <span
                        className="label-typed text-[9px] text-ink-faint tracking-[0.15em]"
                        title="Detected from your IP — click 'precise' for GPS accuracy"
                    >
                        auto · IP
                    </span>
                )}
                {data.location?.source === "browser_geolocation" && (
                    <span className="label-typed text-[9px] text-stamp-green tracking-[0.15em]">
                        precise · GPS
                    </span>
                )}
                <button
                    onClick={() => setShowSearch((v) => !v)}
                    className="ml-auto text-[9px] text-ink-soft hover:text-stamp-red font-body underline inline-flex items-center gap-1"
                    title="Change location"
                    data-testid="weather-change-location"
                >
                    <Search className="w-3 h-3" />
                    change
                </button>
                {data.location?.source !== "browser_geolocation" && (
                    <button
                        onClick={usePreciseLocation}
                        disabled={saving}
                        className="text-[9px] text-ink-soft hover:text-stamp-red font-body underline inline-flex items-center gap-1"
                        title="Upgrade to GPS-accurate location"
                        data-testid="weather-precise-location-inline"
                    >
                        <Crosshair className="w-3 h-3" />
                        precise
                    </button>
                )}
                <button
                    onClick={() => {
                        setTuningOpen(true);
                        setTuningInput(String(data.reference_hours_per_day || 8));
                    }}
                    className="text-[9px] text-ink-soft hover:text-stamp-red font-body underline"
                    title={`Tune ceiling · currently ${data.reference_hours_per_day}h per full-sun day`}
                    data-testid="weather-tune"
                >
                    tune
                </button>
            </div>

            {/* Hero score */}
            <div className="flex items-end gap-6 mb-4 flex-wrap">
                <div>
                    <div className="label-typed text-[9px]">Today's power</div>
                    <div
                        className="font-heading text-5xl text-ink mt-1 tracking-tight"
                        data-testid="weather-power-score"
                    >
                        {Math.round(score * 100)}<span className="text-xl text-ink-soft">%</span>
                    </div>
                    <div className="font-heading uppercase tracking-[0.2em] text-[10px] text-stamp-red mt-1">
                        {today.band || "—"}
                    </div>
                </div>
                <div className="flex-1 border-l-2 border-ink/15 pl-5">
                    <div className="label-typed text-[9px] mb-1">Driven by</div>
                    <div className="font-body text-xs text-ink leading-relaxed">
                        Yesterday:{" "}
                        <span className="font-heading">
                            {today.yesterday_hours != null ? `${today.yesterday_hours}h` : "—"}
                        </span>{" "}
                        sunshine ·{" "}
                        Day-before:{" "}
                        <span className="font-heading">
                            {today.day_before_hours != null ? `${today.day_before_hours}h` : "—"}
                        </span>
                    </div>
                    <div className="font-body text-[10px] text-ink-soft mt-1 italic">
                        Two sunny days → full power · Two dull → zero. Tomorrow shifts on today's sky.
                    </div>
                </div>
            </div>

            {/* 8-day trend strip */}
            <div className="flex items-end gap-1" data-testid="weather-trend">
                {trend.map((d) => {
                    const h = Math.max(2, (d.score || 0) * maxBarHeight);
                    const isToday = d.date === today.date;
                    return (
                        <div
                            key={d.date}
                            className="flex-1 flex flex-col items-center gap-1"
                            title={`${d.date}: ${Math.round((d.score || 0) * 100)}% · ${d.band}`}
                        >
                            <div
                                className={`w-full transition-all ${isToday ? "bg-stamp-red" : "bg-ink/40"}`}
                                style={{ height: `${h}px` }}
                            />
                            <div className={`font-body text-[8px] uppercase tracking-wider ${isToday ? "text-stamp-red font-bold" : "text-ink-soft"}`}>
                                {d.date.slice(5)}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Inline city-search panel */}
            {showSearch && (
                <div className="mt-4 pt-4 border-t border-ink/10" data-testid="weather-search-panel">
                    <PlaceSearchInput
                        value={searchQ}
                        onChange={(v) => { setSearchQ(v); searchPlaces(v); }}
                        results={searchResults}
                        onPick={pickPlace}
                        searching={searching}
                        saving={saving}
                        autoFocus
                    />
                    <div className="mt-2 flex items-center justify-between">
                        <div className="label-typed text-[9px] text-ink-faint">
                            City, town, or village. Any country.
                        </div>
                        <button
                            onClick={() => { setShowSearch(false); setSearchQ(""); setSearchResults([]); }}
                            className="text-xs font-body text-ink-soft hover:text-stamp-red inline-flex items-center gap-1"
                        >
                            <X className="w-3 h-3" /> Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Tuning */}
            {tuningOpen && (
                <div className="mt-4 pt-4 border-t border-ink/10 flex items-center gap-2 flex-wrap" data-testid="weather-tune-form">
                    <div className="label-typed text-[9px] whitespace-nowrap">Full-sun day = </div>
                    <input
                        type="number"
                        step="0.5"
                        min="1"
                        max="16"
                        value={tuningInput}
                        onChange={(e) => setTuningInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && saveTuning()}
                        autoFocus
                        data-testid="weather-tune-input"
                        className="w-20 px-2 py-1 border border-ink/30 bg-paper-light font-body text-sm text-ink focus:outline-none focus:border-stamp-red"
                    />
                    <div className="label-typed text-[9px]">hours sunshine</div>
                    <button
                        onClick={saveTuning}
                        disabled={saving}
                        data-testid="weather-tune-save"
                        className="ml-auto inline-flex items-center gap-2 px-3 py-1 border border-stamp-red text-stamp-red font-heading uppercase tracking-[0.18em] text-[10px] hover:bg-stamp-red hover:text-paper disabled:opacity-50"
                    >
                        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                        Save
                    </button>
                    <button
                        onClick={() => { setTuningOpen(false); setTuningInput(""); }}
                        className="text-xs font-body text-ink-soft hover:text-stamp-red"
                    >
                        Cancel
                    </button>
                </div>
            )}
        </section>
    );
}

function PlaceSearchInput({ value, onChange, results, onPick, searching, saving, autoFocus }) {
    return (
        <div className="relative">
            <div className="flex items-center gap-2">
                <Search className="w-3 h-3 text-ink-soft" />
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="e.g. Tokyo · Brisbane · Buenos Aires"
                    autoFocus={autoFocus}
                    data-testid="weather-place-search-input"
                    className="flex-1 px-3 py-1.5 border border-ink/30 bg-paper-light font-body text-sm text-ink focus:outline-none focus:border-stamp-red"
                />
                {searching && <Loader2 className="w-3 h-3 animate-spin text-stamp-red" />}
            </div>
            {results.length > 0 && (
                <div
                    className="mt-1 border border-ink/20 bg-paper max-h-64 overflow-y-auto"
                    data-testid="weather-place-results"
                >
                    {results.map((r, idx) => (
                        <button
                            key={`${r.label}-${idx}`}
                            onClick={() => onPick(r)}
                            disabled={saving}
                            data-testid={`weather-place-result-${idx}`}
                            className="block w-full text-left px-3 py-2 border-b border-ink/10 last:border-b-0 hover:bg-paper-light disabled:opacity-50"
                        >
                            <div className="font-body text-sm text-ink">
                                {r.name}
                                {r.country_code && <span className="text-ink-faint text-[10px] ml-2">[{r.country_code}]</span>}
                            </div>
                            <div className="font-body text-[10px] text-ink-soft">
                                {[r.admin1, r.country].filter(Boolean).join(", ")}
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
